import * as _ from "lodash";
import { ColumnConfigPreference, ColumnIds } from "./common";
import { Column } from "../../../../common/components/ColumnsSelector/ColumnsSelector";

/**
 * Migrations for column configuration in localstorage
 * Please check the following points:
 *
 * - make them idempotent (eg by checking previous version number)
 * - make them pure functions (clone instead of mutate)
 * - pay attention to order in migration array
 * - make sure ot increase version number
 */

function v1ToV2(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 1) {
		return {
			...previous,
			data: previous.data.filter(
				(column) => !["shape", "bidaskshape"].includes(column.id)
			),
			version: 2,
		};
	}
	return previous;
}

function v2ToV3(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 2) {
		return {
			...previous,
			data: [
				...previous.data,
				{
					label: "customer p50",
					id: ColumnIds.CUSTOMER_P50,
					disabled: false,
				},
			],
			version: 3,
		};
	}
	return previous;
}

function v3ToV4(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 3) {
		previous.data.push({
			label: "Click granularity",
			id: ColumnIds.CLICKABILITY_GRANULARITY,
			disabled: false,
		});
	}
	return previous;
}

function v4ToV5(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 4) {
		const newData = [
			...previous.data,
			{ label: "shape", id: ColumnIds.SHAPE },
			{ label: "bidaskshape", id: "bidaskshape" as ColumnIds },
		];
		return { ...previous, data: _.uniqBy(newData, "id"), version: 5 };
	}
	return previous;
}

function v5ToV6(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 5) {
		return {
			...previous,
			data: [
				...previous.data,
				{
					id: ColumnIds.CLEANING_WARNING,
					label: "Warning",
					disabled: false,
				},
			],
			version: 6,
		};
	}
	return previous;
}

function v6ToV7(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 6) {
		const allColumns: Column<ColumnIds>[] = [
			...previous.data,
			{ label: "Setup fee", id: ColumnIds.SETUPFEE },
			{
				label: "Imbalance cost provision",
				id: ColumnIds.IMBALANCECOSTPROVISION,
			},
			{
				label: "Profile cost provision",
				id: ColumnIds.PROFILECOSTPROVISION,
			},
			{
				label: "Delta profile cost provision",
				id: ColumnIds.DELTAPROFILECOSTPROVISION,
			},
			{ label: "Shape provision", id: ColumnIds.SHAPEPROVISION },
			{
				label: "Shape Bid Ask provision",
				id: ColumnIds.SHAPEBIDASKPROVISION,
			},
		];
		const v7ColumnOrder = [
			ColumnIds.PGID,
			ColumnIds.PID,
			ColumnIds.PRID,
			ColumnIds.TOURBILLON_REF,
			ColumnIds.PR_STATUS,
			ColumnIds.PR_SITE,
			ColumnIds.PR_ALPHA_OR_BETA,
			ColumnIds.P_TYPE_NAME,
			ColumnIds.P_TECHNO_NAME,
			ColumnIds.PRICING_DATE,
			ColumnIds.P_VALIDITY_DATE,
			ColumnIds.P_PARTY,
			ColumnIds.P_BASE_CAPA,
			ColumnIds.CLICKABILITY,
			ColumnIds.CLICKABILITY_GRANULARITY,
			ColumnIds.DELIVERY_PERIOD,
			ColumnIds.P_INSTALL_CAPA,
			ColumnIds.P_CHAPTER_51,
			ColumnIds.CUSTOMER_P50,
			ColumnIds.P50,
			ColumnIds.PROXY,
			ColumnIds.LF,
			ColumnIds.QTY,
			ColumnIds.CROSS,
			ColumnIds.GOO_CROSS,
			ColumnIds.GOO_ADJ,
			ColumnIds.XBC,
			ColumnIds.XPC,
			ColumnIds.XDPC,
			ColumnIds.SETUPFEE,
			ColumnIds.IMBALANCECOSTPROVISION,
			ColumnIds.PROFILECOSTPROVISION,
			ColumnIds.DELTAPROFILECOSTPROVISION,
			ColumnIds.SHAPEPROVISION,
			ColumnIds.SHAPEBIDASKPROVISION,
			ColumnIds.P50_PROV,
			ColumnIds.SHAPE,
			ColumnIds.MARGIN_PROV,
			ColumnIds.GOO_PRICE,
			ColumnIds.MARGIN_PROVISION,
			ColumnIds.ALPHA_BASELOAD,
			ColumnIds.BETA,
			ColumnIds.INDIC,
			ColumnIds.PR_ALPHABASELOADCLIENT,
			ColumnIds.PR_INDICCLIENT,
			ColumnIds.PR_FIXING_DT,
			ColumnIds.PR_BASELOAD_PRICE,
			ColumnIds.PR_BASELOAD_MID,
			ColumnIds.PR_COMMERCIAL_MARGIN,
			ColumnIds.PR_FIXPRICE,
			ColumnIds.PR_BETAFIX,
			ColumnIds.PR_CONTRACT_ID,
			ColumnIds.CLEANING_WARNING,
		];
		return {
			...previous,
			data: v7ColumnOrder
				.map((columnId) =>
					_.find(allColumns, (column) => column.id === columnId)
				)
				.filter((column) => !!column) as Column<ColumnIds>[],
			version: 7,
		};
	}
	return previous;
}

function v7ToV8(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 7) {
		const allColumns: Column<ColumnIds>[] = previous.data;
		// Re-order columns + defualt hide provisions
		const v7ColumnOrder = [
			ColumnIds.PGID,
			ColumnIds.PID,
			ColumnIds.PRID,
			ColumnIds.TOURBILLON_REF,
			ColumnIds.PR_STATUS,
			ColumnIds.PR_SITE,
			ColumnIds.PR_ALPHA_OR_BETA,
			ColumnIds.P_TYPE_NAME,
			ColumnIds.P_TECHNO_NAME,
			ColumnIds.PRICING_DATE,
			ColumnIds.P_VALIDITY_DATE,
			ColumnIds.P_PARTY,
			ColumnIds.P_BASE_CAPA,
			ColumnIds.CLICKABILITY,
			ColumnIds.CLICKABILITY_GRANULARITY,
			ColumnIds.DELIVERY_PERIOD,
			ColumnIds.P_INSTALL_CAPA,
			ColumnIds.P_CHAPTER_51,
			ColumnIds.CUSTOMER_P50,
			ColumnIds.P50,
			ColumnIds.PROXY,
			ColumnIds.LF,
			ColumnIds.QTY,
			ColumnIds.CROSS,
			ColumnIds.GOO_CROSS,
			ColumnIds.GOO_ADJ,
			ColumnIds.XBC,
			ColumnIds.XPC,
			ColumnIds.XDPC,
			ColumnIds.SHAPE,
			ColumnIds.P50_PROV,
			ColumnIds.MARGIN_PROV,
			ColumnIds.GOO_PRICE,
			ColumnIds.MARGIN_PROVISION,
			ColumnIds.ALPHA_BASELOAD,
			ColumnIds.BETA,
			ColumnIds.INDIC,
			ColumnIds.PR_ALPHABASELOADCLIENT,
			ColumnIds.PR_INDICCLIENT,
			ColumnIds.PR_FIXING_DT,
			ColumnIds.PR_BASELOAD_PRICE,
			ColumnIds.PR_BASELOAD_MID,
			ColumnIds.PR_COMMERCIAL_MARGIN,
			ColumnIds.PR_FIXPRICE,
			ColumnIds.PR_BETAFIX,
			ColumnIds.PR_CONTRACT_ID,
			ColumnIds.CLEANING_WARNING,
		];
		return {
			...previous,
			data: v7ColumnOrder
				.map((columnId) =>
					_.find(allColumns, (column) => column.id === columnId)
				)
				.filter((column) => !!column) as Column<ColumnIds>[],
			version: 8,
		};
	}
	return previous;
}

function v8ToV9(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 8) {
		return {
			...previous,
			data: [
				...previous.data,
				{
					label: "Goo Commercial Margin",
					id: ColumnIds.FIXINGGOOCOMMERCIALMARGIN,
				},
				{ label: "Goo Final Price", id: ColumnIds.FINALGOOPRICE },
			],
			version: 9,
		};
	}
	return previous;
}

function v9ToV10(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 9) {
		const data = previous.data;
		data.push({
			label: "Negative price",
			id: ColumnIds.NEGATIVEPRICEVALUE,
		});
		return { ...previous, version: 10, data };
	}

	return previous;
}

function v10ToV11(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 10) {
		const data = previous.data;
		data.push({
			label: "Hedge Ratio",
			id: ColumnIds.HEDGERATIOPERCENT,
		});
		return { ...previous, version: 11, data };
	}

	return previous;
}

function v11ToV12(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 11) {
		const data = previous.data;
		data.push({ label: "Balancing Group", id: ColumnIds.BALANCING_GROUP });
		return { ...previous, version: 12, data };
	}

	return previous;
}

function v12ToV13(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 12) {
		const data = previous.data;
		data.push({ label: "Alpha Index", id: ColumnIds.ALPHAINDEX });
		data.push({
			label: "Total Power Margin",
			id: ColumnIds.TOTALPOWERMARGIN,
		});
		data.push({ label: "Total GOO Margin", id: ColumnIds.TOTALGOOMARGIN });
		return { ...previous, version: 13, data };
	}

	return previous;
}

function v13ToV14(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 13) {
		const data = [...previous.data];
		data.push({
			label: "GOO Power adjustment (input)",
			id: ColumnIds.GOO_ADJ_INPUT,
		});
		return { ...previous, version: 14, data };
	}

	return previous;
}

function v14ToV15(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 14) {
		const data = [...previous.data];
		data.push({
			label: "alpha provision",
			id: ColumnIds.ALPHA_PROVISION,
		});
		data.push({
			label: "beta provision",
			id: ColumnIds.BETA_PROVISION,
		});
		return { ...previous, version: 15, data };
	}

	return previous;
}

function v15ToV16(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 15) {
		const data = [...previous.data];
		data.push({
			label: "Alpha Baseload Margin",
			id: ColumnIds.ALPHA_BASELOAD_MARGIN,
		});
		data.push({
			label: "Alpha Index Margin",
			id: ColumnIds.ALPHA_INDEX_MARGIN,
		});
		data.push({
			label: "Beta Margin",
			id: ColumnIds.BETA_MARGIN,
		});
		data.push({
			label: "Alpha Index Client",
			id: ColumnIds.ALPHA_INDEX_CLIENT,
		});
		data.push({
			label: "Beta Client",
			id: ColumnIds.BETA_CLIENT,
		});
		return { ...previous, version: 16, data };
	}

	return previous;
}

function v16Tov17(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 16) {
		const data = [...previous.data];
		data.push({
			label: "Tender",
			id: ColumnIds.TENDER,
		});
		return { ...previous, version: 17, data };
	}
	return previous;
}

function v17Tov18(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 17) {
		const data = [...previous.data];
		data.push({
			label: "§51 number of hours",
			id: ColumnIds.P_CHAPTER_51_NUMBER_OF_HOURS,
		});
		return { ...previous, version: 18, data };
	}
	return previous;
}

function v18Tov19(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 18) {
		const data = [...previous.data];
		data.push({
			label: "Negative price conditions",
			id: ColumnIds.NEGATIVE_PRICE_CONDITIONS,
		});
		return { ...previous, version: 19, data };
	}
	return previous;
}

function v19Tov20(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 19) {
		const data = [...previous.data];
		data.push({
			label: "Curtailability forward conditions",
			id: ColumnIds.CURTAILABILITY_FORWARD_CONDITIONS,
		});
		return { ...previous, version: 20, data };
	}
	return previous;
}
function v20Tov21(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 20) {
		const data = [...previous.data];
		data.push(
			{
				label: "Is Nodal",
				id: ColumnIds.IS_NODAL,
			},
			{
				label: "Block Shape",
				id: ColumnIds.BLOCK_SHAPE,
			},
			{
				label: "Energy Bid Ask",
				id: ColumnIds.ENERGY_BID_ASK,
			},
			{
				label: "Production Variability Bid Ask",
				id: ColumnIds.PRODUCTION_VARIABILITY_BID_ASK,
			},
			{
				label: "Credit Costs",
				id: ColumnIds.CREDIT_COSTS,
			}
		);
		return { ...previous, version: 21, data };
	}
	return previous;
}

function v21ToV22(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 21) {
		const data = [...previous.data];
		data.push({ label: "Cap/Floor", id: ColumnIds.CAP_FLOOR });
		return { ...previous, version: 22, data };
	}
	return previous;
}

function v22ToV23(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 22) {
		const data = [...previous.data];
		data.push({ label: "Settl. period", id: ColumnIds.SETTLEMENT_PERIOD });
		return { ...previous, version: 23, data };
	}
	return previous;
}

function v23ToV24(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 23) {
		const data = [...previous.data];
		data.push({ label: "Provision type", id: ColumnIds.P_PROVISION_TYPE });
		return { ...previous, version: 24, data };
	}
	return previous;
}

function v24ToV25(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 24) {
		const data = [...previous.data];
		data.push({ label: "Spot market", id: ColumnIds.P_SPOT_MARKET });
		return { ...previous, version: 25, data };
	}
	return previous;
}

function v25ToV26(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 25) {
		const data = [...previous.data];
		data.push({ label: "Pricing Add Ons", id: ColumnIds.PRICING_ADD_ONS });
		return { ...previous, version: 26, data };
	}
	return previous;
}

function v26toV27(previous: ColumnConfigPreference): ColumnConfigPreference {
	if (previous.version === 26) {
		const data = [...previous.data];
		data.push({ label: "Shape Type", id: ColumnIds.SHAPE_TYPE });
		return { ...previous, version: 27, data };
	}
	return previous;
}

const migrations = [
	v1ToV2,
	v2ToV3,
	v3ToV4,
	v4ToV5,
	v5ToV6,
	v6ToV7,
	v7ToV8,
	v8ToV9,
	v9ToV10,
	v10ToV11,
	v11ToV12,
	v12ToV13,
	v13ToV14,
	v14ToV15,
	v15ToV16,
	v16Tov17,
	v17Tov18,
	v18Tov19,
	v19Tov20,
	v20Tov21,
	v21ToV22,
	v22ToV23,
	v23ToV24,
	v24ToV25,
	v25ToV26,
	v26toV27,
];
const migrateFn = _.flow(migrations);

export const LATEST_COLUMN_VERSION = 27;

export function migrate(
	previous: ColumnConfigPreference
): ColumnConfigPreference {
	return migrateFn(previous);
}
